import { Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle, CheckCircle, FileText, Scale } from 'lucide-react';
import Navbar from '../components/Navbar';

const VorschriftenInfo = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <Link
          to="/"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück zur Übersicht
        </Link>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center gap-4 mb-8">
            <div className="bg-blue-100 p-3 rounded-lg">
              <Scale className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Deutsche Vorschriften für Wartungsprotokolle
              </h1>
              <p className="text-gray-600 mt-1">
                Gültig ab 2024/2025
              </p>
            </div>
          </div>

          {/* F-Gase-Verordnung */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  F-Gase-Verordnung (EU) 2024/573
                </h2>
                <p className="text-gray-600 mb-4">
                  Gilt für Kälte- und Klimaanlagen mit fluorierten Treibhausgasen
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-blue-900 mb-3">Pflichtangaben im Wartungsprotokoll:</h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Kältemitteltyp und Füllmenge</strong> (in kg)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>GWP-Wert</strong> (Global Warming Potential)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>CO₂-Äquivalent</strong> (Berechnung: Füllmenge × GWP / 1000)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Dichtheitsprüfung</strong> durchgeführt (Datum und Ergebnis)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Nächster Prüftermin</strong> (abhängig vom CO₂-Äquivalent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Nachfüllmenge</strong> (falls Kältemittel nachgefüllt wurde)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Herkunft des Kältemittels</strong> (Lieferant, Chargennummer)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Zertifikatsnummer des Technikers</strong> (Kat. I oder II gem. ChemKlimaschutzV)</span>
                </li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Prüfintervalle für Dichtheitsprüfungen:</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-amber-200">
                    <th className="text-left py-2 text-amber-900">CO₂-Äquivalent</th>
                    <th className="text-left py-2 text-amber-900">Prüfintervall</th>
                  </tr>
                </thead>
                <tbody className="text-amber-800">
                  <tr className="border-b border-amber-100">
                    <td className="py-2">&lt; 5 t CO₂</td>
                    <td className="py-2">Empfohlen jährlich (keine Pflicht)</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="py-2">5 - 50 t CO₂</td>
                    <td className="py-2 font-semibold">Jährlich (Pflicht)</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="py-2">50 - 500 t CO₂</td>
                    <td className="py-2 font-semibold">Halbjährlich (Pflicht)</td>
                  </tr>
                  <tr>
                    <td className="py-2">&gt; 500 t CO₂</td>
                    <td className="py-2 font-semibold">Vierteljährlich + Leckageerkennungssystem (Pflicht)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* VDI 6022 */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  VDI 6022 - Hygiene in raumlufttechnischen Anlagen
                </h2>
                <p className="text-gray-600 mb-4">
                  Gilt für Klima- und Lüftungsanlagen
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="font-semibold text-green-900 mb-3">Pflichtangaben:</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Hygieneinspektionen</strong> durchgeführt</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Luftfilter</strong> geprüft/gereinigt/gewechselt (mit Filterklasse)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Kondensatwanne und -ablauf</strong> gereinigt/geprüft</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Wärmetauscher</strong> gereinigt</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Bakterien-/Schimmelbefall</strong> dokumentiert</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Mikrobiologische Untersuchungen</strong> (falls erforderlich)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>Nächste Hygieneprüfung</strong> (Termin)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* GWP-Werte */}
          <div className="mb-8">
            <div className="flex items-start gap-3 mb-4">
              <FileText className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Häufige GWP-Werte von Kältemitteln
                </h2>
                <p className="text-gray-600 mb-4">
                  Zur Berechnung des CO₂-Äquivalents
                </p>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 text-gray-900">Kältemittel</th>
                    <th className="text-left py-2 text-gray-900">GWP-Wert</th>
                    <th className="text-left py-2 text-gray-900">Umweltfreundlichkeit</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R744 (CO₂)</td>
                    <td className="py-2 font-semibold">1</td>
                    <td className="py-2 text-green-600">✓ Sehr umweltfreundlich</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R290 (Propan)</td>
                    <td className="py-2 font-semibold">3</td>
                    <td className="py-2 text-green-600">✓ Sehr umweltfreundlich</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R600a (Isobutan)</td>
                    <td className="py-2 font-semibold">3</td>
                    <td className="py-2 text-green-600">✓ Sehr umweltfreundlich</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R32</td>
                    <td className="py-2 font-semibold">675</td>
                    <td className="py-2 text-blue-600">○ Mittel</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R1234yf</td>
                    <td className="py-2 font-semibold">4</td>
                    <td className="py-2 text-green-600">✓ Umweltfreundlich</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2">R134a</td>
                    <td className="py-2 font-semibold">1.430</td>
                    <td className="py-2 text-amber-600">⚠ Hoch</td>
                  </tr>
                  <tr>
                    <td className="py-2">R410A</td>
                    <td className="py-2 font-semibold">2.088</td>
                    <td className="py-2 text-red-600">✗ Sehr hoch (ab 2025 Einschränkungen)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Wärmepumpen-Vorschriften */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Wärmepumpen-Vorschriften
                </h2>
                <p className="text-gray-600 mb-4">
                  Spezifische Regelungen für Wärmepumpen-Anlagen
                </p>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-green-900 mb-3">Relevante Normen:</h3>
              <ul className="space-y-2 text-green-800">
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>VDI 4645</strong> - Heizungsanlagen mit Wärmepumpen (Planung, Errichtung, Betrieb)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>VDMA 24020</strong> - Richtlinie für Wartung von Wärmepumpen</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>FW 309 Teil 1</strong> - Berechnung der Normnutzungsgrade und des Primärenergiebedarfs</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>F-Gase-Verordnung</strong> - Für kältemittelhaltige Wärmepumpen</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Wichtige Prüfpunkte:</h4>
              <p className="text-sm text-gray-700">
                COP/Leistungszahl, Quellentemperatur (Luft/Sole/Wasser), Vorlauf-/Rücklauftemperaturen, 
                Solekreislauf (Frostschutzmittel), Abtaufunktion, Betriebsstunden
              </p>
            </div>
          </div>

          {/* Klimakammer-Vorschriften */}
          <div className="mb-8 pb-8 border-b border-gray-200">
            <div className="flex items-start gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Klimakammer-Vorschriften
                </h2>
                <p className="text-gray-600 mb-4">
                  Regelungen für Klimaprüfschränke und -kammern
                </p>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-4">
              <h3 className="font-semibold text-purple-900 mb-3">Relevante Normen:</h3>
              <ul className="space-y-2 text-purple-800">
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>DIN 12880</strong> - Klimaprüfschränke und Klimaprüfräume</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>DIN EN 60068</strong> - Umweltprüfungen (Temperatur, Feuchte)</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>F-Gase-Verordnung</strong> - Für kältemittelhaltige Klimakammern</span>
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span><strong>DAkkS-Kalibrierung</strong> - Temperatur- und Feuchtesensoren (empfohlen jährlich)</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Wichtige Prüfpunkte:</h4>
              <p className="text-sm text-gray-700">
                Temperaturbereich (Soll/Ist min/max), Luftfeuchte (% rF), Sensorkalibrierung, 
                Türdichtungen, Temperaturhomogenität im Prüfraum, Regelgenauigkeit
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Wichtiger Hinweis:</h3>
                <p className="text-blue-800 text-sm">
                  Die konformen Wartungsprotokoll-Vorlagen in diesem System entsprechen den aktuellen gesetzlichen 
                  Anforderungen der <strong>F-Gase-Verordnung (EU) 2024/573</strong>, <strong>VDI 6022</strong>, 
                  <strong>VDI 2050</strong>, <strong>VDI 4645</strong>, <strong>DIN 12880</strong> und 
                  <strong>DIN EN 60068</strong>. Alle Pflichtfelder sind entsprechend gekennzeichnet und werden automatisch 
                  in die PDF-Exporte übernommen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VorschriftenInfo;
